import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ConnectorCatalogEntry } from '../types';
import { getConnectorIcon } from './ConnectorIcons';

interface ConnectorSidebarItemProps {
  connector: ConnectorCatalogEntry;
  /** Add this connector to the board. The same thing a drop does. */
  onAdd: (connector: ConnectorCatalogEntry) => void;
  /**
   * Why this one cannot be added, as a translation key, or null when it can.
   *
   * A key rather than a boolean: an item that is simply greyed out replaces
   * "nothing happened when I clicked" with "nothing happens when I click", and
   * the vendor still does not know that the board holds one connector at a
   * time. The reason is the whole point of disabling it.
   */
  unavailableReasonKey?: string | null;
}

export const ConnectorSidebarItem: React.FC<ConnectorSidebarItemProps> = ({
  connector,
  onAdd,
  unavailableReasonKey,
}) => {
  const { t } = useTranslation();
  const disabled = !!unavailableReasonKey;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `connector-${connector.type}`,
    data: { connectorType: connector.type },
    // Dragging something that cannot be dropped anywhere is the same dead end
    // by a longer route.
    disabled,
  });

  const IconComponent = getConnectorIcon(connector.type);

  const item = (
    <div
      ref={setNodeRef}
      {...(disabled ? {} : listeners)}
      {...attributes}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      data-testid={`connector-item-${connector.type}`}
      /*
       * Click adds it, drag still works.
       *
       * Dragging was the only way in, and it was ceremony: the drop handler
       * discards the position and centres the flow on the canvas, so the whole
       * gesture only ever carried "this connector" — which a click carries
       * too. The pointer sensor has an 8px activation distance, so a click and
       * a drag do not compete.
       */
      onClick={() => { if (!disabled) onAdd(connector); }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onAdd(connector);
        }
      }}
      className={cn(
        'flex select-none items-center gap-3 rounded-lg border border-transparent px-3 py-2 transition-colors',
        'focus-visible:border-ring focus-visible:outline-none',
        disabled
          ? 'cursor-not-allowed opacity-45'
          : isDragging
            ? 'cursor-grabbing opacity-50'
            : 'cursor-pointer hover:border-border hover:bg-accent',
      )}
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted">
        {/* Brand mark keeps its brand colour; everything else is monochrome. */}
        <IconComponent className="size-[18px]" style={{ color: connector.color }} />
      </span>
      <span className="text-sm font-medium text-foreground">{t(connector.nameKey)}</span>
    </div>
  );

  if (!disabled) return item;

  return (
    <Tooltip>
      {/* Not asChild on a disabled-looking div: the trigger needs its own
          element to keep receiving hover once pointer handlers are gone. */}
      <TooltipTrigger asChild>{item}</TooltipTrigger>
      <TooltipContent side="right">{t(unavailableReasonKey!)}</TooltipContent>
    </Tooltip>
  );
};
